/**
 * RecordExpenseModal — office-staff quick action (Prompt 11E.8 §6).
 *
 * Two-step submit: create the expense (to get an id), then — if a bill was
 * attached — upload it via a second request. A failed attachment upload
 * does not roll back the (already-recorded) expense; it surfaces as a
 * separate error so the operator can retry the upload from the Expenses
 * list instead of re-entering the whole form.
 *
 * data-testid: candeza-expenses-record-modal
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal, Button } from '@/components/common';
import {
  useCreateExpense,
  uploadExpenseAttachment,
  EXPENSE_CATEGORY_OPTIONS,
  type ExpenseCategory,
} from '@/features/expenses/api/expenses';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 };
const fieldStyle: React.CSSProperties = { marginBottom: 14 };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid rgba(148,163,184,0.4)',
  fontSize: 14,
};

export interface RecordExpenseModalProps {
  onClose: () => void;
}

export const RecordExpenseModal: React.FC<RecordExpenseModalProps> = ({ onClose }) => {
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('other');
  const [vendor, setVendor] = useState('');
  const [note, setNote] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const createExpense = useCreateExpense();

  const submit = async () => {
    const paise = Math.round((Number(amount) || 0) * 100);
    if (paise <= 0) {
      toast.error('Amount must be greater than zero');
      return;
    }
    try {
      const expense = await createExpense.mutateAsync({
        date,
        amount: paise,
        category,
        ...(vendor.trim() ? { vendor: vendor.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (attachment) {
        setUploading(true);
        try {
          await uploadExpenseAttachment(expense.id, attachment);
        } catch {
          toast.error('Expense recorded, but the attachment failed to upload — retry it from the Expenses list.');
          onClose();
          return;
        } finally {
          setUploading(false);
        }
      }
      toast.success('Expense recorded');
      onClose();
    } catch {
      // The api client's own toast already surfaces the failure.
    }
  };

  const busy = createExpense.isPending || uploading;

  return (
    <Modal isOpen onClose={onClose} title="Record Expense" size="md" data-testid="candeza-expenses-record-modal">
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="expense-date">Date *</label>
        <input
          id="expense-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={inputStyle}
          data-testid="candeza-expenses-record-date"
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="expense-amount">Amount (₹) *</label>
        <input
          id="expense-amount"
          type="number"
          min={0}
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={inputStyle}
          data-testid="candeza-expenses-record-amount"
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="expense-category">Category *</label>
        <select
          id="expense-category"
          value={category}
          onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
          style={inputStyle}
          data-testid="candeza-expenses-record-category"
        >
          {EXPENSE_CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="expense-vendor">Vendor</label>
        <input
          id="expense-vendor"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          style={inputStyle}
          data-testid="candeza-expenses-record-vendor"
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="expense-note">Note</label>
        <textarea
          id="expense-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
          data-testid="candeza-expenses-record-note"
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="expense-attachment">Bill / receipt</label>
        <input
          id="expense-attachment"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
          data-testid="candeza-expenses-record-attachment"
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} isLoading={busy} data-testid="candeza-expenses-record-submit">
          Record Expense
        </Button>
      </div>
    </Modal>
  );
};
